/**
 * Offer-time roll primitives + the `rollDraftOffer` / `rollOffers`
 * composers that produce DraftOffer instances at the moment a draft
 * modal would open. Each primitive consumes exactly one rng() call so
 * the canonical draw order (Section 6 of the Pokédex design doc) stays
 * byte-stable against the determinism CI gate.
 *
 * The rng parameter is `() => number` (a function bound to a seedStore
 * domain via seedStore.rngFor('ivs') etc.) — never seedStore directly,
 * so unit tests can drive these with a seedrandom('test-seed')-style
 * stream without bringing the whole singleton.
 */

import { NATURES, type Nature } from '../data/natures';
import { loadGen3 } from '../data/dex';
import { getMovesAtLevel } from '../data/levelUpMoves';
import { getEggAndTmMoves } from '../data/eggMoves';
import {
  DRAFT_POOLS,
  weightedPick,
  type DraftPoolEntry,
} from '../data/draftPools';
import { SPECIES_TO_DEX } from '../battle/teamFromParty';
import { randomUUID } from './uuid';
import { gameAbilityDesc } from '../data/abilityDescOverrides';
import type { BaseStats } from '../data/computeStat';
import type { CaughtAt, Gender, IvSet } from './runState';

export type SeededRng = () => number;

/**
 * Full offer-time snapshot for a single draft card. Each draft modal
 * surfaces 3 of these (or fewer when the pool shrinks). The `id` is
 * pre-generated here so the same uuid transfers from offer-modal into
 * PartyMember when the player picks — load-bearing for the Pokédex's
 * Skipped tab (offer that was skipped still carries full stats forever
 * keyed by this id).
 *
 * Display fields (displayName, dexId, types, abilityDesc, movesDisplay,
 * baseStats) are denormalized from @pkmn/dex at rollDraftOffer time so
 * DraftScene + PokedexScene + PokemonFullSheet render sync without
 * awaiting the dex chunk per row. The card layout shows much of this
 * inline (Step 5b — show don't tell), so denormalizing avoids a
 * per-row dex roundtrip.
 */
export interface DraftOffer {
  id: string;
  species: string;
  displayName: string;
  dexId: number;
  types: string[];
  level: number;
  /** @pkmn move ids (lowercase, no spaces) — what sim consumes. */
  moves: string[];
  /** Title-case move names for UI ("Thunder Shock", "Tail Whip"). Aligned
   *  with moves[] by index. */
  movesDisplay: string[];
  nature: Nature;
  ivs: IvSet;
  /** @pkmn ability id (lowercase, no separators — "static", "overgrow"). */
  ability: string;
  /** Display name ("Static", "Overgrow") + short description for the
   *  card's ability row. shortDesc is one sentence at most. */
  abilityName: string;
  abilityDesc: string;
  /** Base stats from gen-3 species data; used by PokemonFullSheet to
   *  compute the in-battle stat alongside each IV bar. */
  baseStats: BaseStats;
  eggPullMove?: string;
  isRarePull: boolean;
  /** Cartridge-faithful gender, resolved via @pkmn/dex species metadata
   *  (species.gender for fixed-gender + genderless species; species.
   *  genderRatio for ratio-based species). Rolled at offer time as the
   *  LAST draw in the 'ivs' canonical sequence. Memoir thread — surfaces
   *  on the draft card + Pokédex + careerStore eulogy. v9. */
  gender: Gender;
  /** Where + when the offer was rolled — caller-provided from runState.
   *  getPosition() at the WorldScene.rollOffers call site. WorldScene
   *  MUST assert position is non-null before constructing the payload
   *  (precondition violation, not a runtime path). Plumbs into
   *  PartyMember.caughtAt + PartyMemberSnapshot.caughtAt + (optional)
   *  CareerEntry.caughtAt on commit. v9. */
  caughtAt: CaughtAt;
}

/** 0-31 uniform IV roll. Cartridge-faithful range. */
export function rollIv(rng: SeededRng): number {
  return Math.floor(rng() * 32);
}

/** Uniform nature pick from the 25-nature cartridge set. Order matches
 *  data/natures.NATURES so the determinism hash stays stable. */
export function rollNature(rng: SeededRng): Nature {
  const idx = Math.floor(rng() * NATURES.length);
  return NATURES[idx]!;
}

/**
 * Pick an ability for the given species from `@pkmn/dex`. Async because
 * the dex chunk is lazy-loaded (eager-warmed at boot per Step 1; cold
 * cache falls through to the loader). Picks uniformly from the species'
 * Gen-3 ability list (primary `0` + optional secondary `1`; no Hidden
 * Abilities in Gen 3).
 *
 * Consumes one rng() call unconditionally so the determinism stream
 * advances by the same amount regardless of the species' ability count
 * — Section 6 of the design doc treats this draw as part of the
 * canonical 'ivs' sequence (post-nature, pre-egg-move).
 *
 * Defensive: an unknown species (content gap) returns '' but still
 * consumes the draw. Caller can treat '' as "ability not yet known".
 */
export async function rollAbility(species: string, rng: SeededRng): Promise<string> {
  const gen = await loadGen3();
  const sp = gen.species.get(species);
  const candidates: string[] = [];
  if (sp) {
    if (sp.abilities['0']) candidates.push(sp.abilities['0']);
    if (sp.abilities['1']) candidates.push(sp.abilities['1']);
  }
  // Always consume one draw to keep the canonical order stable.
  const roll = rng();
  if (candidates.length === 0) return '';
  const idx = Math.floor(roll * candidates.length);
  // Normalize to @pkmn-style lowercase id (e.g. 'Solar Power' → 'solarpower').
  return candidates[idx]!.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Resolve a Pokémon's gender at offer-time using cartridge-faithful
 * @pkmn/dex species metadata. The algorithm:
 *
 *   1. ALWAYS consume one rng() draw before branching. Determinism
 *      invariant — same discipline as rollAbility (line 108).
 *   2. `species.gender === 'N'` → genderless (Magnemite, Voltorb, Staryu).
 *   3. `species.gender === 'M'` / `'F'` → fixed gender (rare in gen 3 —
 *      Nidoran♂/♀, Tauros, Latios, Hitmonchan, etc.).
 *   4. Otherwise read `species.genderRatio: { M: number, F: number }`:
 *      roll < ratio.M → male; else female. Bulbasaur is { M: 0.875,
 *      F: 0.125 } — a ~1-in-8 ♀ is the memorable rare event.
 *
 * Defensive fall-throughs (unknown species, missing genderRatio) return
 * 'genderless' + log a warn — same posture as rollAbility's '' fallback.
 * The draw is consumed in every branch so the 'ivs' canonical sequence
 * stays byte-stable regardless of content.
 */
export async function rollGender(species: string, rng: SeededRng): Promise<Gender> {
  const gen = await loadGen3();
  const sp = gen.species.get(species);
  // ALWAYS consume one draw BEFORE any branching — determinism invariant.
  const roll = rng();
  if (!sp) {
    console.warn('[draftRoll] unknown species in rollGender:', species);
    return 'genderless';
  }
  if (sp.gender === 'N') return 'genderless';
  if (sp.gender === 'M') return 'male';
  if (sp.gender === 'F') return 'female';
  const ratio = sp.genderRatio;
  if (!ratio) {
    console.warn(
      '[draftRoll] species has neither gender nor genderRatio:',
      species,
    );
    return 'genderless';
  }
  return roll < ratio.M ? 'male' : 'female';
}

/** Capitalize the first letter of a lowercase species id for display. */
function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Inclusive uniform integer pick in [min, max]. Consumes one rng draw. */
function randIntInRange([min, max]: readonly [number, number], rng: SeededRng): number {
  return min + Math.floor(rng() * (max - min + 1));
}

/**
 * Roll a single full DraftOffer for `species` at a level drawn from
 * `levelRange`. The canonical draw order (Section 6):
 *
 *   'draft' domain:
 *     - level (one draw)
 *     - isRarePull check (one draw, always consumed)
 *     - eggPullMove pick (one CONDITIONAL draw; consumed only when
 *       the rare-pull rolled <0.15 AND the species has a non-empty
 *       egg/TM pool — design's "silently no rare pull when pool is
 *       empty" rule keeps the offer normal-looking in that case)
 *
 *   await dex lookup for moves + species metadata (non-rng)
 *
 *   'ivs' domain:
 *     - 6 IVs in stat order (hp, atk, def, spa, spd, spe)
 *     - nature
 *     - ability
 *     - gender (v9 — appended at the TAIL so future derivative-
 *       attribute fields can also be appended without disturbing
 *       existing fixture bytes for the IVs / nature / ability prefix)
 *
 * Total: 2 'draft' draws (+1 conditional) + 9 'ivs' draws per offer.
 * The conditional draw is the source of byte-stability divergence
 * between Step 3 and Step 4 — Step 3 always skipped it (eggPullMove
 * was hardcoded undefined); Step 4 wires it for real, so the
 * encounter-determinism snapshot needs re-recording.
 *
 * `caughtAt` is caller-provided — the offer-roller has no map context.
 * WorldScene.rollOffers MUST assert runState.getPosition() !== null
 * before constructing the payload (precondition violation, not a
 * runtime path; v9 type contract is non-nullable mapName).
 */
export async function rollDraftOffer(
  species: string,
  levelRange: readonly [number, number],
  draftRng: SeededRng,
  ivsRng: SeededRng,
  caughtAt: CaughtAt,
): Promise<DraftOffer> {
  const id = randomUUID();

  // --- 'draft' domain ---
  const level = randIntInRange(levelRange, draftRng);
  // isRarePull draw is always consumed (Section 6 order stability).
  const rarePullRoll = draftRng();
  const baseMoves = await getMovesAtLevel(species, level);
  let eggPullMove: string | undefined;
  let moves: string[] = baseMoves;
  if (rarePullRoll < 0.15) {
    const eggPool = await getEggAndTmMoves(species);
    if (eggPool.length > 0) {
      // Conditional draw — only fires when the rare-pull is going to
      // actually land. Empty pool collapses to "silently no rare pull"
      // (defensive path a + design Section 5) without burning the draw.
      const idx = Math.floor(draftRng() * eggPool.length);
      const picked = eggPool[idx]!;
      eggPullMove = picked;
      // Slot the move into position 4 (cartridge would prompt the
      // player; v0.1 has no replace-which-move modal so we shift
      // slot 4 silently). If the species has < 4 base moves at this
      // level, append. Defensive path (i).
      moves =
        baseMoves.length < 4
          ? [...baseMoves, picked]
          : [...baseMoves.slice(0, 3), picked];
    }
  }
  const isRarePull = !!eggPullMove;

  // --- dex lookups (cached; first call resolves the lazy chunk) ---
  const gen = await loadGen3();
  const sp = gen.species.get(species);
  // Title-case move display names from the dex (e.g. "Thunder Shock").
  // Falls back to capitalize(id) on a missing move — defensive against
  // future @pkmn/dex shape shifts. Aligned with moves[] by index so
  // sim consumers (id form) and UI consumers (display form) stay
  // index-parallel.
  const movesDisplay: string[] = moves.map((mid) => {
    const m = gen.moves.get(mid);
    return m?.name ?? capitalize(mid);
  });
  // Base stats for the in-battle stat computation alongside each IV
  // bar in the full sheet. v0.1 species always resolve via @pkmn/dex;
  // an unknown species defaults to all-zeros (PokemonFullSheet treats
  // 0 as "no data" and falls back to the IV-only display).
  const bs = sp?.baseStats;
  const baseStats: BaseStats = bs
    ? { hp: bs.hp, atk: bs.atk, def: bs.def, spa: bs.spa, spd: bs.spd, spe: bs.spe }
    : { hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0 };

  // --- 'ivs' domain ---
  const ivs: IvSet = {
    hp: rollIv(ivsRng),
    atk: rollIv(ivsRng),
    def: rollIv(ivsRng),
    spa: rollIv(ivsRng),
    spd: rollIv(ivsRng),
    spe: rollIv(ivsRng),
  };
  const nature = rollNature(ivsRng);
  const ability = await rollAbility(species, ivsRng);
  // Ability display + short description from the dex. The card surfaces
  // "Ability: Static · paralyzes attackers on contact" so the player
  // doesn't have to memorize what each name means. shortDesc is the
  // one-liner; the longer .desc is available if we ever need it. On
  // an unresolved ability (defensive), fall back to capitalize(id)
  // + empty desc.
  const abilityRef = ability ? gen.abilities.get(ability) : null;
  const abilityName = abilityRef?.name ?? (ability ? capitalize(ability) : '');
  // Game-register desc, not Showdown's competitive shortDesc ("No
  // competitive use." — playtest round 2). Override table falls back
  // to the dex text for unlisted abilities.
  const abilityDesc = gameAbilityDesc(ability ?? '', abilityRef?.shortDesc ?? '');
  // Gender is the TAIL of the 'ivs' sequence so any future derivative-
  // attribute field (shiny chance, held item, …) can also be appended
  // without disturbing the prefix's byte stability. Consumes one ivsRng
  // draw unconditionally per the rollGender invariant.
  const gender = await rollGender(species, ivsRng);

  return {
    id,
    species,
    displayName: sp?.name ?? capitalize(species),
    dexId: sp?.num ?? SPECIES_TO_DEX[species] ?? 0,
    types: (sp?.types as readonly string[] | undefined)?.slice() ?? [],
    level,
    moves,
    movesDisplay,
    nature,
    ivs,
    ability,
    abilityName,
    abilityDesc,
    baseStats,
    eggPullMove,
    isRarePull,
    gender,
    caughtAt: { ...caughtAt },
  };
}

/**
 * Roll the full set of offers for a draft modal. Samples
 * `pool.pickCount` entries from `DRAFT_POOLS[zoneId]` after filtering
 * out `excludedSpecies` (party species + already-drafted species, in
 * the WorldScene caller). Anti-duplication within the offer set is
 * built-in (each weightedPick removes the entry from the remaining
 * pool). When `available.length < pickCount` the pool shrinks rather
 * than backfilling from elsewhere — friend-group game; an awkwardly-
 * short modal beats a fabricated species the zone doesn't canonically
 * have.
 *
 * Each picked entry then resolves via rollDraftOffer in left-to-right
 * order (offer 0, 1, 2). Section 6 of the design doc fixes this
 * sequence for byte-stable determinism.
 */
export async function rollOffers(
  zoneId: string,
  draftRng: SeededRng,
  ivsRng: SeededRng,
  excludedSpecies: ReadonlySet<string>,
  caughtAt: CaughtAt,
): Promise<DraftOffer[]> {
  const pool = DRAFT_POOLS[zoneId];
  if (!pool) return [];
  const remaining: DraftPoolEntry[] = pool.pool.filter(
    (e) => !excludedSpecies.has(e.species),
  );
  const targetCount = Math.min(pool.pickCount, remaining.length);
  const picks: DraftPoolEntry[] = [];
  for (let i = 0; i < targetCount; i++) {
    const entry = weightedPick(remaining, (e) => e.weight, draftRng);
    picks.push(entry);
    remaining.splice(remaining.indexOf(entry), 1);
  }
  const offers: DraftOffer[] = [];
  for (const entry of picks) {
    offers.push(
      await rollDraftOffer(entry.species, entry.levelRange, draftRng, ivsRng, caughtAt),
    );
  }
  return offers;
}
